import { v4 as uuid } from 'uuid';
import { openSpinWheelProgress, playSpinWheel } from '../../API/api';
import { normalizeSpinWheelProgress } from './spinWheelProgress';
import { normalizeSpinWheelItem } from './spinWheelItems';

const SESSION_ID_STORAGE_KEY = 'sessionId';

export const getOrCreateSessionId = () => {
  const storedSessionId = localStorage.getItem(SESSION_ID_STORAGE_KEY);

  if (storedSessionId && storedSessionId !== 'undefined' && storedSessionId !== 'null') {
    return storedSessionId;
  }

  const sessionId = `user${uuid()}`;
  localStorage.setItem(SESSION_ID_STORAGE_KEY, sessionId);

  return sessionId;
};

// Minutes between the user's clock and UTC, positive west of UTC - exactly
// what the server needs to work out which calendar day this visit falls on.
const getTimezoneOffsetMinutes = () => new Date().getTimezoneOffset();

const readResponseJson = async (response) => {
  const responseText = typeof response?.text === 'function' ? await response.text() : '';

  if (!responseText.trim()) {
    return {};
  }

  return JSON.parse(responseText);
};

const assertOkResponse = (response, actionName) => {
  if (!response?.ok) {
    throw new Error(`${actionName} failed with ${response?.status || 'unknown'} status`);
  }
};

/**
 * Counts today's visit on the server and returns the progress it holds.
 * `becameEligible` is true on the exact visit that unlocked the wheel, so the
 * home page can announce it once.
 */
export const loadProgressRecordWithStatus = async (sessionId) => {
  const response = await openSpinWheelProgress(sessionId, getTimezoneOffsetMinutes());

  assertOkResponse(response, 'Opening spin wheel progress');

  const body = await readResponseJson(response);

  return {
    becameEligible: Boolean(body.became_eligible),
    progress: normalizeSpinWheelProgress(body.progress ?? body, sessionId),
  };
};

export const loadProgressRecord = async (sessionId) => {
  const { progress } = await loadProgressRecordWithStatus(sessionId);

  return progress;
};

/**
 * Plays the session's spin. The server decides the outcome; this only shapes
 * the answer for the page. Resolves to null when the server has no spin for
 * this session, so the page can refresh what it shows.
 */
export const spinWheel = async (sessionId) => {
  const response = await playSpinWheel(sessionId, getTimezoneOffsetMinutes());

  if (response?.status === 409) {
    return null;
  }

  assertOkResponse(response, 'Spinning the wheel');

  const body = await readResponseJson(response);

  if (!body?.item) {
    throw new Error('Spinning the wheel returned no prize');
  }

  const result = body.result ?? {};
  const item = normalizeSpinWheelItem(body.item);

  return {
    progress: normalizeSpinWheelProgress(body.progress, sessionId),
    item,
    result: {
      type: item.type,
      isDuplicate: Boolean(result.is_duplicate),
      collectionComplete: Boolean(result.collection_complete),
      reward: result.reward ? normalizeSpinWheelItem(result.reward) : null,
    },
  };
};
